import type { AssistantTask, CrowdexAssistant, TaskAutomation } from '@crowdex/agent-contracts/api';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';
import { isDesktopPreviewPath, useWorkspaceDirectory } from '@/lib/assistants/directory';
import {
  type AssistantConversationApi,
  type AssistantMemberView,
  type AssistantOverview,
  useAssistantConversationApi,
} from './assistant-conversation-api';
import { PREVIEW_PERSONAS } from './assistant-preview-data';

/** The assistant whose channel this is; null else. */
export function useAssistantOfConversation(conversationId: string | null): CrowdexAssistant | null {
  const wanted = conversationId;
  return useWorkspaceDirectory(
    (state) =>
      (wanted === null
        ? undefined
        : state.projects.find((project) => project.conversationId === wanted)) ?? null,
  );
}

/** The saved agents the assistant hands work to (its Agents): the boards' on `/desktop-preview…`. */
export function useAssistantPersonas(
  project: Pick<CrowdexAssistant, 'memberAgentIds'> | null,
): AssistantMemberView[] {
  const { profiles } = useAccountAgentProfiles();
  if (project === null) return [];
  const known: AssistantMemberView[] = isDesktopPreviewPath()
    ? PREVIEW_PERSONAS
    : profiles.map((profile) => ({
        agentId: profile.id,
        name: profile.name,
        agent: profile.agentId,
        ...(profile.avatar === undefined ? {} : { avatar: profile.avatar }),
      }));
  return project.memberAgentIds.flatMap((id) => known.filter((entry) => entry.agentId === id));
}

type Entry = {
  overview: AssistantOverview | null;
  tasks: AssistantTask[] | null;
  error: string | null;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setTimeout> | null;
  api: AssistantConversationApi;
  /** A snapshot `useSyncExternalStore` compares by identity. */
  snapshot: AssistantState;
};

export type AssistantState = {
  overview: AssistantOverview | null;
  tasks: AssistantTask[] | null;
  error: string | null;
};

const EMPTY: AssistantState = { overview: null, tasks: null, error: null };
const entries = new Map<string, Entry>();

async function load(projectId: string, entry: Entry): Promise<void> {
  try {
    const [overview, tasks] = await Promise.all([
      entry.api.overview(projectId),
      entry.api.tasks(projectId),
    ]);
    entry.overview = overview;
    entry.tasks = tasks;
    entry.error = null;
  } catch (error) {
    entry.error = error instanceof Error ? error.message : String(error);
  }
  entry.snapshot = { overview: entry.overview, tasks: entry.tasks, error: entry.error };
  for (const listener of entry.listeners) listener();
}

function entryOf(projectId: string, api: AssistantConversationApi): Entry {
  let entry = entries.get(projectId);
  if (entry === undefined) {
    entry = {
      overview: null,
      tasks: null,
      error: null,
      listeners: new Set(),
      timer: null,
      api,
      snapshot: EMPTY,
    };
    entries.set(projectId, entry);
  }
  return entry;
}

/** Reads a project again now (after a change, or when the server says it changed). */
export async function refreshAssistant(projectId: string): Promise<void> {
  const entry = entries.get(projectId);
  if (entry !== undefined) await load(projectId, entry);
}

/** Tests start from nothing. */
export function resetAssistantState(): void {
  for (const entry of entries.values()) if (entry.timer !== null) clearTimeout(entry.timer);
  entries.clear();
}

/**
 * The project's overview and tasks, shared by its panel tabs and its heading: read when one of
 * them shows, then again whenever the server says a placement or a run changed (its tasks).
 */
export function useAssistantState(project: Pick<CrowdexAssistant, 'id'> | null) {
  const api = useAssistantConversationApi();
  // The api a project is first read with stays its own (a new one each render would read again).
  const apiRef = useRef(api);
  apiRef.current = api;
  const projectId = project?.id ?? null;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (projectId === null) return () => undefined;
      const entry = entryOf(projectId, apiRef.current);
      entry.listeners.add(listener);
      if (entry.listeners.size === 1) void load(projectId, entry);
      return () => {
        entry.listeners.delete(listener);
        if (entry.listeners.size === 0 && entry.timer !== null) {
          clearTimeout(entry.timer);
          entry.timer = null;
        }
      };
    },
    [projectId],
  );
  const state = useSyncExternalStore(
    subscribe,
    () => (projectId === null ? EMPTY : (entries.get(projectId)?.snapshot ?? EMPTY)),
    () => EMPTY,
  );
  useEffect(() => {
    if (projectId === null) return;
    return onStreamedEvent((event) => {
      if (event.type === 'crowdex/workspaces.changed' || event.type === 'crowdex/task.ended')
        void refreshAssistant(projectId);
    });
  }, [projectId]);
  return state;
}

/** What an assistant runs among the user's automations (`assistantAutomationList`). */
export type AssistantAutomationList = {
  automations: TaskAutomation[];
  default: TaskAutomation | null;
};

/**
 * Pure: the automations listed that it may run (those it names; none named: all), and its default
 * among them (none: its tasks are done by an agent alone).
 */
export function assistantAutomationList(
  listed: readonly TaskAutomation[],
  project: Pick<CrowdexAssistant, 'automationIds' | 'defaultAutomation'>,
): AssistantAutomationList {
  const ids = project.automationIds ?? [];
  const automations =
    ids.length === 0 ? [...listed] : listed.filter((automation) => ids.includes(automation.id));
  return {
    automations,
    default: automations.find((automation) => automation.id === project.defaultAutomation) ?? null,
  };
}

/** The automations the assistant runs, once its overview is read; null before. */
export function useAssistantAutomations(
  project: CrowdexAssistant | null,
): AssistantAutomationList | null {
  const { overview } = useAssistantState(project);
  if (project === null || overview === null) return null;
  return assistantAutomationList(overview.automations ?? [], project);
}
